import { Dialog } from '@ori-ui/components';
import { Button, Typography } from '@ori-ui/mui';
import useTestId from '@ori/testid-generator';
import { useCallback, type FC } from 'react';

import { useTranslations } from '../../hooks';
import type { GetGeolocationData } from '../../utils';
import { setHideGeolocationUntilDate } from '../../utils/localStorageUtils';
import { DIALOG, FOREIGN_COUNTRY_BUTTON, HOME_COUNTRY_BUTTON } from './constants';
import { Content, Inner } from './styles';

interface GeolocationDialogProps extends Pick<GetGeolocationData, 'foreignCountry' | 'homeCountry' | 'homeCountryUrl'> {
  /** If true dialog will be visible. */
  open: boolean;
  /** Function to close dialog. */
  onClose: () => void;
}

/**
 * Dialog that includes information for user that he is accessing foreign country tenant.
 */
export const GeolocationDialog: FC<GeolocationDialogProps> = ({
  foreignCountry,
  homeCountry,
  homeCountryUrl,
  open,
  onClose,
}) => {
  const { translations, formatMessage } = useTranslations();
  const { getTestId } = useTestId();

  const onCloseAndHideUntilDate = useCallback(() => {
    onClose();
    setHideGeolocationUntilDate();
  }, [onClose]);

  return (
    <Dialog
      data-testid={getTestId(DIALOG)}
      open={open}
      onClose={onCloseAndHideUntilDate}
    >
      <Content>
        <Inner>
          <Typography
            variant="h4"
            component="h4"
          >
            {formatMessage('dialogTitle', { foreignCountry, homeCountry })}
          </Typography>
          <Typography>{translations.dialogMessage}</Typography>
        </Inner>
        <Inner>
          <Button
            data-testid={getTestId(HOME_COUNTRY_BUTTON)}
            variant="contained"
            color="primary"
            href={homeCountryUrl}
            onClick={onClose}
          >
            {formatMessage('homeCountryButton', { homeCountry })}
          </Button>
          <Button
            data-testid={getTestId(FOREIGN_COUNTRY_BUTTON)}
            variant="contained"
            color="secondary"
            onClick={onCloseAndHideUntilDate}
          >
            {formatMessage('foreignCountryButton', { foreignCountry })}
          </Button>
        </Inner>
      </Content>
    </Dialog>
  );
};
